import React from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { Briefcase, Users, Building2, FileText } from "lucide-react";
import { motion } from "framer-motion";
import { dashboardList } from "../../../../services/postService";
import JobDashboard from "./JobDashboard";
import ApplicationDashboard from "./ApplicationDashboard";

/* ================= RECRUITER DASHBOARD ================= */
function RecruiterDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-counts"],
    queryFn: async () => {
      const res = await dashboardList();
      if (res?.status !== 200) {
        throw new Error(res?.message || "Failed to fetch dashboard data");
      }
      return res?.data;
    },
  });

  const stats = [
    {
      title: "Total Jobs",
      value: data?.totalJobs || 0,
      icon: Briefcase,
      gradient: "from-blue-500 to-blue-600",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
    },

    {
      title: "Total Applications",
      value: data?.totalApplications || 0,
      icon: FileText,
      gradient: "from-amber-500 to-amber-600",
      iconBg: "bg-amber-100",
      iconColor: "text-amber-600",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-50 to-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl font-bold text-slate-900 mb-2">
              Dashboard Overview
            </h1>
            <p className="text-slate-600 text-lg">
              Here’s what’s happening on your platform.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {isLoading
            ? Array.from({ length: 2 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 animate-pulse"
                >
                  <div className="h-12 w-12 bg-slate-200 rounded-xl mb-6" />
                  <div className="h-4 bg-slate-200 rounded w-2/3 mb-3" />
                  <div className="h-8 bg-slate-200 rounded w-1/2" />
                </div>
              ))
            : stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={stat.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    whileHover={{ y: -4 }}
                    className="group bg-white rounded-2xl shadow-sm hover:shadow-xl border border-slate-200 hover:border-slate-300 transition-all duration-300 p-6 relative overflow-hidden"
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}
                    />

                    <div className="relative">
                      <div className="flex items-start justify-between mb-6">
                        <div
                          className={`${stat.iconBg} p-3 rounded-xl group-hover:scale-110 transition-transform duration-300`}
                        >
                          <Icon className={`w-6 h-6 ${stat.iconColor}`} />
                        </div>
                      </div>

                      <p className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-2">
                        {stat.title}
                      </p>
                      <h2 className="text-4xl font-bold text-slate-900">
                        {stat.value.toLocaleString()}
                      </h2>
                    </div>
                  </motion.div>
                );
              })}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <JobDashboard />
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <ApplicationDashboard />
          </div>
        </div>
      </div>
    </div>
  );
}
function UserDashboard() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-50 to-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-10">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">
            My Dashboard
          </h1>
          <p className="text-slate-600 text-lg">
            Track latest jobs and my current applications.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <JobDashboard />
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <ApplicationDashboard />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= COMMON DASHBOARD ================= */
export default function CommonDashboard() {
  const { session } = useSelector((state) => state?.authProvider);
  const user = session?.user;

  if (user?.role === "recruiter") {
    return <RecruiterDashboard />;
  }
  if (user?.role === "user") {
    return <UserDashboard />;
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="bg-white shadow-xl rounded-3xl px-12 py-10 text-center"
      >
        <h1 className="text-4xl font-bold text-slate-800 mb-4">
          Welcome {user?.firstName} 👋
        </h1>
      </motion.div>
    </div>
  );
}
